/**
 * OverviewTab -- Read-only summary tab for the UserAdminPanel.
 *
 * Shows account info, hero KPI stats, upstream policy visualization,
 * active sessions with progress, and top libraries/genres with usage bars.
 */
import { useMemo } from "react";

import { Badge } from "../../ui/Badge";
import { Icon } from "../../ui/Icon";
import { cn } from "../../../utils/cn";
import {
  truncateText,
  TRUNCATE_MEDIA_TITLE,
} from "../../../utils/truncateText";
import type { UserProfile } from "../../../hooks/useUserProfile";
import type { WSDSession } from "../../../hooks/useWSDSessions";
import {
  formatDuration,
  formatTimestamp,
  formatBitrate,
} from "./helpers";

// ============================================================================
// Sub-components
// ============================================================================

/** Hero KPI card with accent color stripe and colored icon */
const KpiCard = ({
  icon,
  label,
  value,
  accent,
}: {
  icon: string;
  label: string;
  value: string;
  accent: "violet" | "emerald" | "amber" | "sky" | "rose" | "slate";
}) => {
  const accentMap = {
    violet: {
      border: "border-l-violet-500",
      iconBg: "bg-violet-500/10",
      iconColor: "text-violet-400",
    },
    emerald: {
      border: "border-l-emerald-500",
      iconBg: "bg-emerald-500/10",
      iconColor: "text-emerald-400",
    },
    amber: {
      border: "border-l-amber-500",
      iconBg: "bg-amber-500/10",
      iconColor: "text-amber-400",
    },
    sky: {
      border: "border-l-sky-500",
      iconBg: "bg-sky-500/10",
      iconColor: "text-sky-400",
    },
    rose: {
      border: "border-l-rose-500",
      iconBg: "bg-rose-500/10",
      iconColor: "text-rose-400",
    },
    slate: {
      border: "border-l-slate-500",
      iconBg: "bg-muted/20",
      iconColor: "text-muted-foreground",
    },
  };
  const a = accentMap[accent];

  return (
    <div
      className={cn(
        "rounded-lg border border-border/50 bg-card",
        "border-l-2",
        a.border,
        "p-3 flex items-center gap-3",
      )}
    >
      <div className={cn("rounded-md p-2 shrink-0", a.iconBg)}>
        <Icon icon={icon} className={cn("h-4 w-4 2xl:h-5 2xl:w-5", a.iconColor)} />
      </div>
      <div className="min-w-0">
        <div className="text-lg 2xl:text-xl font-bold text-foreground leading-tight tabular-nums">
          {value}
        </div>
        <div className="text-[10px] 2xl:text-xs text-muted-foreground leading-tight">
          {label}
        </div>
      </div>
    </div>
  );
};

/** Single policy capability chip with colored dot */
const PolicyChip = ({
  label,
  enabled,
  variant = "boolean",
  value,
}: {
  label: string;
  enabled?: boolean;
  variant?: "boolean" | "value";
  value?: string;
}) => (
  <div className="flex items-center gap-2 py-1">
    {variant === "boolean" ? (
      <span
        className={cn(
          "h-2 w-2 rounded-full shrink-0",
          enabled ? "bg-emerald-500" : "bg-muted-foreground/25",
        )}
      />
    ) : (
      <Icon icon="lucide:gauge" className="h-3 w-3 text-muted-foreground/50 shrink-0" />
    )}
    <span className="text-xs 2xl:text-sm text-muted-foreground">{label}</span>
    {variant === "value" && value && (
      <span className="text-xs 2xl:text-sm font-medium text-foreground ml-auto tabular-nums">
        {value}
      </span>
    )}
  </div>
);

/** Horizontal usage bar for library/genre items */
const UsageBar = ({
  label,
  count,
  maxCount,
  icon,
  accent = "primary",
}: {
  label: string;
  count: number;
  maxCount: number;
  icon: string;
  accent?: "primary" | "violet" | "amber";
}) => {
  const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
  const barColor =
    accent === "violet"
      ? "bg-violet-500/40"
      : accent === "amber"
        ? "bg-amber-500/40"
        : "bg-primary/40";

  return (
    <div className="flex items-center gap-2.5 py-1">
      <Icon icon={icon} className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <span className="text-xs 2xl:text-sm text-foreground truncate">{label}</span>
          <span className="text-[10px] 2xl:text-xs text-muted-foreground tabular-nums shrink-0">
            {count.toLocaleString()}
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-muted/30 overflow-hidden">
          <div
            className={cn("h-full rounded-full transition-all", barColor)}
            style={{ width: `${Math.max(pct, 2)}%` }}
          />
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Types
// ============================================================================

export type OverviewTabProps = {
  profile: UserProfile;
  sessions: WSDSession[];
};

// ============================================================================
// Component
// ============================================================================

export const OverviewTab = ({ profile, sessions }: OverviewTabProps) => {
  const userSessions = useMemo(
    () => sessions.filter((s) => s.userId === profile.userId),
    [sessions, profile.userId],
  );

  const maxLibraryCount = useMemo(
    () => Math.max(...(profile.favorites?.topLibraries.map((l) => l.count) ?? [0]), 1),
    [profile.favorites],
  );

  const maxGenreCount = useMemo(
    () => Math.max(...(profile.favorites?.topGenres.map((g) => g.count) ?? [0]), 1),
    [profile.favorites],
  );

  // Policy capabilities grouped by category
  const policyGroups = useMemo(
    () => [
      {
        title: "Access",
        items: [
          { label: "Remote Access", enabled: profile.policy.enableRemoteAccess },
          { label: "Media Playback", enabled: profile.policy.enableMediaPlayback },
        ],
      },
      {
        title: "Transcoding",
        items: [
          { label: "Video", enabled: profile.policy.enableVideoPlaybackTranscoding },
          { label: "Audio", enabled: profile.policy.enableAudioPlaybackTranscoding },
          { label: "Sync", enabled: profile.policy.enableSyncTranscoding },
        ],
      },
      {
        title: "Content",
        items: [
          { label: "Deletion", enabled: profile.policy.enableContentDeletion },
          { label: "Download", enabled: profile.policy.enableContentDownloading },
        ],
      },
    ],
    [profile.policy],
  );

  return (
    <div className="space-y-5">
      {/* Hero KPI Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 gap-2.5">
        <KpiCard
          icon="lucide:clock"
          label="Watch Time"
          value={formatDuration(profile.stats.totalWatchTimeSecs)}
          accent="violet"
        />
        <KpiCard
          icon="lucide:play"
          label="Sessions"
          value={profile.stats.sessionsCount.toLocaleString()}
          accent="emerald"
        />
        <KpiCard
          icon="lucide:percent"
          label="Avg Completion"
          value={`${Math.round(profile.stats.avgCompletionPct)}%`}
          accent="amber"
        />
        <KpiCard
          icon="lucide:monitor-smartphone"
          label="Devices"
          value={profile.stats.uniqueDevices.toLocaleString()}
          accent="sky"
        />
        <KpiCard
          icon="lucide:library"
          label="Unique Titles"
          value={profile.stats.uniqueTitles.toLocaleString()}
          accent="rose"
        />
        <KpiCard
          icon="lucide:calendar"
          label="First Seen"
          value={formatTimestamp(profile.stats.firstSeenAt)}
          accent="slate"
        />
      </div>

      {/* Upstream Policy */}
      <div>
        <h4 className="text-[10px] 2xl:text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-1.5">
          Upstream Policy
        </h4>
        <div className="rounded-lg border border-border/50 bg-card">
          {/* Limits row */}
          <div className="grid grid-cols-2 gap-0 border-b border-border/30">
            <div className="px-3 py-2 border-r border-border/30">
              <PolicyChip
                label="Stream Limit"
                variant="value"
                value={
                  profile.policy.simultaneousStreamLimit != null &&
                  profile.policy.simultaneousStreamLimit > 0
                    ? String(profile.policy.simultaneousStreamLimit)
                    : "Unlimited"
                }
              />
            </div>
            <div className="px-3 py-2">
              <PolicyChip
                label="Bitrate Limit"
                variant="value"
                value={
                  profile.policy.remoteClientBitrateLimit != null &&
                  profile.policy.remoteClientBitrateLimit > 0
                    ? formatBitrate(profile.policy.remoteClientBitrateLimit)
                    : "Unlimited"
                }
              />
            </div>
          </div>

          {/* Capability groups */}
          <div className="grid grid-cols-3 gap-0 divide-x divide-border/30">
            {policyGroups.map((group) => (
              <div key={group.title} className="px-3 py-2">
                <div className="text-[9px] 2xl:text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-0.5">
                  {group.title}
                </div>
                {group.items.map((item) => (
                  <PolicyChip
                    key={item.label}
                    label={item.label}
                    enabled={item.enabled}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Active Sessions */}
      {userSessions.length > 0 && (
        <div>
          <h4 className="text-[10px] 2xl:text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-1.5">
            Active Sessions ({userSessions.length})
          </h4>
          <div className="space-y-1.5">
            {userSessions.map((s) => {
              const mediaIcon =
                s.mediaDetails?.type === "Movie"
                  ? "lucide:film"
                  : s.mediaDetails?.type === "Episode"
                    ? "lucide:tv"
                    : s.mediaDetails?.type === "Live TV"
                      ? "lucide:radio"
                      : "lucide:play";
              const title = s.mediaDetails?.title ?? "Unknown";
              const progress = s.progress?.percentComplete ?? 0;

              return (
                <div
                  key={s.id}
                  className="rounded-lg border border-border/50 bg-card px-3 py-2"
                >
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <Icon
                        icon={mediaIcon}
                        className="h-3.5 w-3.5 text-muted-foreground shrink-0"
                      />
                      <span className="text-xs 2xl:text-sm font-medium text-foreground truncate">
                        {truncateText(title, TRUNCATE_MEDIA_TITLE).displayText}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] 2xl:text-xs text-muted-foreground truncate max-w-[100px]">
                        {s.device.customName ?? s.device.deviceName}
                      </span>
                      {s.playMethod && (
                        <Badge
                          variant={
                            s.playMethod === "Transcode"
                              ? "danger"
                              : s.playMethod === "DirectStream"
                                ? "warning"
                                : "success"
                          }
                          size="xs"
                          className="text-[9px]"
                        >
                          {s.playMethod === "DirectPlay"
                            ? "Direct"
                            : s.playMethod === "DirectStream"
                              ? "Stream"
                              : "Transcode"}
                        </Badge>
                      )}
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1 rounded-full bg-muted/30 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary/60 transition-all"
                        style={{ width: `${Math.max(progress, 1)}%` }}
                      />
                    </div>
                    <span className="text-[9px] 2xl:text-[10px] text-muted-foreground tabular-nums shrink-0">
                      {Math.round(progress)}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top Libraries + Top Genres side by side when both present */}
      {profile.favorites && (
        profile.favorites.topLibraries.length > 0 || profile.favorites.topGenres.length > 0
      ) && (
        <div className={cn(
          "grid gap-4",
          profile.favorites.topLibraries.length > 0 && profile.favorites.topGenres.length > 0
            ? "grid-cols-1 md:grid-cols-2"
            : "grid-cols-1",
        )}>
          {/* Top Libraries */}
          {profile.favorites.topLibraries.length > 0 && (
            <div>
              <h4 className="text-[10px] 2xl:text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-1.5">
                Top Libraries
              </h4>
              <div className="rounded-lg border border-border/50 bg-card px-3 py-1.5">
                {profile.favorites.topLibraries.map((item) => (
                  <UsageBar
                    key={item.name}
                    label={item.name}
                    count={item.count}
                    maxCount={maxLibraryCount}
                    icon="lucide:library"
                    accent="violet"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Top Genres */}
          {profile.favorites.topGenres.length > 0 && (
            <div>
              <h4 className="text-[10px] 2xl:text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-1.5">
                Top Genres
              </h4>
              <div className="rounded-lg border border-border/50 bg-card px-3 py-1.5">
                {profile.favorites.topGenres.map((item) => (
                  <UsageBar
                    key={item.name}
                    label={item.name}
                    count={item.count}
                    maxCount={maxGenreCount}
                    icon="lucide:drama"
                    accent="amber"
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
